import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { cn } from '@/core/lib/utils';
import { Field, FieldError } from '@/shared/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FloatingLabelSelect } from './FloatingLabelSelect';
import { FormField } from './FormField';
import type { FloatingInputVariant } from './FormFieldInput';

interface SelectOption {
  value: string;
  label: string;
  /** Rendered before the option label inside each SelectItem. */
  icon?: React.ReactNode;
}

interface FormFieldSelectProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Leading icon inside the trigger, before the selected value. */
  startIcon?: React.ReactNode;
  /** Blue outline the design gives every AI-filled control. */
  highlighted?: boolean;
  /** Stores the selection as a number, for integer-coded fields like priority. */
  numeric?: boolean;
  'data-testid'?: string;
  floatingLabel?: boolean;
  floatingVariant?: FloatingInputVariant;
  /**
   * Hide the visible label. For a field whose row already renders its own label —
   * the convert-to-task dialog does — this avoids showing two.
   *
   * The trigger keeps its accessible name: `label` is emitted as `aria-label`, so
   * the field is named even when nothing outside supplies one. A wrapper's own
   * `<label htmlFor>` stays valid alongside it; both carry the same text.
   */
  hideLabel?: boolean;
}

export function FormFieldSelect<T extends FieldValues>({
  options,
  placeholder,
  startIcon,
  highlighted,
  numeric,
  floatingLabel = false,
  floatingVariant = 'default',
  hideLabel = false,
  'data-testid': testId,
  ...props
}: FormFieldSelectProps<T>) {
  const { t } = useTranslation('formField');
  const resolvedPlaceholder = placeholder ?? t('select.placeholder');

  if (floatingLabel) {
    return (
      <Controller
        name={props.name}
        control={props.control}
        render={({ field, fieldState }) => {
          const hasValue = field.value !== '' && field.value !== undefined && field.value !== null;

          return (
            <Field data-invalid={fieldState.invalid}>
              <FloatingLabelSelect
                id={props.name}
                label={props.label}
                value={field.value as string}
                onValueChange={(value) => field.onChange(value)}
                hasValue={hasValue}
                invalid={fieldState.invalid}
                disabled={props.disabled}
                variant={floatingVariant}
                data-testid={testId}
              >
                {options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.icon}
                    {opt.label}
                  </SelectItem>
                ))}
              </FloatingLabelSelect>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          );
        }}
      />
    );
  }

  return (
    <FormField
      {...props}
      hideLabel={hideLabel}
      render={({ field, invalid, disabled }) => {
        const selectValue =
          field.value === undefined || field.value === null ? '' : String(field.value);

        return (
          <Select
            value={selectValue}
            onValueChange={(value) => {
              // Radix Select can call onValueChange when mounting with an empty value.
              // Skip the write if the normalized value equals the current form value to
              // avoid an undefined <-> '' bounce that can trigger an infinite update loop.
              const nextValue = !value
                ? numeric
                  ? undefined
                  : null
                : numeric
                  ? Number(value)
                  : value;
              if (nextValue !== field.value) {
                field.onChange(nextValue);
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={props.name}
              aria-invalid={invalid}
              // Named even with the visible label suppressed; see `hideLabel`.
              aria-label={hideLabel ? props.label : undefined}
              data-testid={testId}
              className={cn(
                'h-10 w-full justify-start *:data-[slot=select-value]:flex-1',
                highlighted && 'border-primary',
              )}
            >
              {startIcon}
              <SelectValue placeholder={resolvedPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.icon}
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }}
    />
  );
}
